// ============================================================================
// HAI Reporting Tool - Export Service
// CSV and PDF export functionality
// ============================================================================

import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { calculateDeviceDays, calculateInfectionRates, formatRate } from '@/utils/calculations';
import type { DeviceSnapshot, HAIRecord, SSISurveillance, Ward, PeriodMetrics } from '@/types';

/**
 * Export data to CSV
 */
export function exportToCSV(
  data: PeriodMetrics[],
  wards: Ward[],
  filename: string = 'hai-report.csv'
): void {
  const wardMap = new Map(wards.map(w => [w.id, w.name]));

  const csvData = data.map(metric => ({
    'Ward': wardMap.get(metric.wardId) || metric.wardId,
    'Period Start': metric.periodStart,
    'Period End': metric.periodEnd,
    'CVP Days': metric.deviceDays.cvp,
    'PL Days': metric.deviceDays.pl,
    'Catheter Days': metric.deviceDays.urinaryCatheter,
    'Ventilator Days': metric.deviceDays.ventilator,
    'CAUTI Cases': metric.cases.uti,
    'CLABSI Cases': metric.cases.cabsi,
    'BSI Other Cases': metric.cases.bsiOther,
    'VAP Cases': metric.cases.vap,
    'CAUTI Rate': formatRate(metric.rates.uti),
    'CLABSI Rate': formatRate(metric.rates.cabsi),
    'BSI Other Rate': formatRate(metric.rates.bsiOther),
    'VAP Rate': formatRate(metric.rates.vap),
    'SSI Procedures': metric.ssiRate?.procedures || 0,
    'SSI Infections': metric.ssiRate?.infections || 0,
    'SSI Rate': metric.ssiRate ? formatRate(metric.ssiRate.rate) : '0.00'
  }));

  const csv = Papa.unparse(csvData);
  downloadFile(csv, filename, 'text/csv');
}

/**
 * Export data to PDF
 */
export function exportToPDF(
  data: PeriodMetrics[],
  wards: Ward[],
  facilityName: string,
  filename: string = 'hai-report.pdf'
): void {
  const doc = new jsPDF();
  const wardMap = new Map(wards.map(w => [w.id, w.name]));

  // Header
  doc.setFontSize(18);
  doc.text('HAI Surveillance Report', 14, 20);

  doc.setFontSize(12);
  doc.text(`Facility: ${facilityName}`, 14, 30);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 36);

  // Summary table
  const tableData = data.map(metric => [
    wardMap.get(metric.wardId) || metric.wardId,
    `${metric.periodStart} to ${metric.periodEnd}`,
    metric.deviceDays.cvp,
    metric.deviceDays.pl,
    metric.deviceDays.urinaryCatheter,
    metric.deviceDays.ventilator,
    metric.cases.uti,
    metric.cases.cabsi,
    metric.cases.bsiOther,
    metric.cases.vap,
    formatRate(metric.rates.uti),
    formatRate(metric.rates.cabsi),
    formatRate(metric.rates.bsiOther),
    formatRate(metric.rates.vap)
  ]);

  autoTable(doc, {
    startY: 45,
    head: [[
      'Ward',
      'Period',
      'CVP Days',
      'PL Days',
      'Cath Days',
      'Vent Days',
      'CAUTI',
      'CLABSI',
      'BSI Other',
      'VAP',
      'CAUTI Rate',
      'CLABSI Rate',
      'BSI Other Rate',
      'VAP Rate'
    ]],
    body: tableData,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [14, 165, 233] },
    margin: { top: 45 }
  });

  // Footer
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }

  doc.save(filename);
}

/**
 * Helper function to trigger file download
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Calculate period metrics from raw data
 */
export function calculatePeriodMetricsFromData(
  wardId: string,
  periodStart: string,
  periodEnd: string,
  deviceSnapshots: DeviceSnapshot[],
  haiRecords: HAIRecord[],
  ssiRecords: SSISurveillance[]
): PeriodMetrics {
  const deviceDays = calculateDeviceDays(deviceSnapshots);

  const cases = haiRecords.reduce((acc, record) => ({
    uti: acc.uti + (record.cases.uti || 0),
    ssiSuperficial: acc.ssiSuperficial + (record.cases.ssiSuperficial || 0),
    ssiDeep: acc.ssiDeep + (record.cases.ssiDeep || 0),
    ssiOrganSpace: acc.ssiOrganSpace + (record.cases.ssiOrganSpace || 0),
    hapOther: acc.hapOther + (record.cases.hapOther || 0),
    vap: acc.vap + (record.cases.vap || 0),
    bsiOther: acc.bsiOther + (record.cases.bsiOther || 0),
    cabsi: acc.cabsi + (record.cases.cabsi || 0),
    sepsis: acc.sepsis + (record.cases.sepsis || 0),
    other: acc.other + (record.cases.other || 0)
  }), { uti: 0, ssiSuperficial: 0, ssiDeep: 0, ssiOrganSpace: 0, hapOther: 0, vap: 0, bsiOther: 0, cabsi: 0, sepsis: 0, other: 0 });

  const rates = calculateInfectionRates(cases, deviceDays);

  const ssiRate = ssiRecords.length > 0 ? {
    procedures: ssiRecords.reduce((sum, r) => sum + r.procedures, 0),
    infections: ssiRecords.reduce((sum, r) => sum + r.infections, 0),
    rate: 0
  } : undefined;

  if (ssiRate && ssiRate.procedures > 0) {
    ssiRate.rate = (ssiRate.infections / ssiRate.procedures) * 100;
  }

  return {
    wardId,
    periodStart,
    periodEnd,
    deviceDays,
    cases,
    rates,
    ssiRate
  };
}
